import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEnvironmentsforUser } from '../api/documentAPI';
import { useMsal } from "@azure/msal-react";
import { useNotification } from '../context/NotificationContext';
import { NotificationType } from '../enums/NotificationType';

import axios from '../api/axios';
import useCookie from "../hooks/useCookie";
import { ALL_FOLDERS } from '../atoms/chat/useChatEnvironmentAtom';
import useAuth from '../hooks/useAuth';
import config from '../config/configJS';
import { applyUserLanguage } from '../services/language';

import '../css/pages/login_page.css';
import '../css/loading_animation.css';

const LoginForm = () => {
  
  const [t] = useTranslation('global');
  const { showNotification } = useNotification();
  const { instance } = useMsal();
  const { setAuth, authConfig } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });

  // Reset form on mount to prevent browser autofill persistence
  useEffect(() => {
    setFormData({ username: '', password: '' });
  }, []);

  // Ak Azure prihlásenie nie je zapnuté, formulár meno+heslo je hlavná cesta -> rozbaľ ho.
  useEffect(() => {
    if (authConfig && !(authConfig.methods && authConfig.methods.azure)) {
      setShowEmailLogin(true);
    }
  }, [authConfig]);

  const [envCookie, setEnvCookie, ] = useCookie("ENV");  

  const handleLogin = async (loginType) => {

    if (loginType === "popup") {
      setIsLoading(true);
      instance.loginPopup({
        scopes: config.adConfig?.scopes
      }).then(async (response) => {

        const accessToken = await instance.acquireTokenSilent({
          account: response.account,
          scopes: config.adConfig?.scopes
        });

        const r1 = await axios.post("/api/auth/Ad", { Token: accessToken.accessToken, IdToken: accessToken.idToken }, { withCredentials: true });
        // fotka používateľa z Graph API (len AD login)
        const adPhoto = r1?.data?.photo ?? r1?.data?.Photo;
        if (adPhoto) {
          localStorage.setItem('userPhoto', adPhoto);
        } else {
          localStorage.removeItem('userPhoto');
        }
        const { data: user } = await axios.post("/api/auth/GetUserByCookie", {}, { withCredentials: true });

        await handlePostLogin(user);
        return false;

      }).catch((error) => {
        console.log(error);
        setIsLoading(false);
      });

    } else if (loginType === "redirect") {
      instance.loginRedirect({
        scopes: config.adConfig?.scopes
        //redirectUri: window.location.origin
      });
    }

    // if (loginType === "redirect") {
    //   try {
    //     setIsLoading(true);
    //     // First check if we have a redirect response
    //     const response = await instance.handleRedir  ectPromise();
        
    //     if (response) {
    //       // If we have a response, process it
    //       const accessToken = await instance.acquireTokenSilent({
    //         account: response.account,
    //         scopes: ["User.Read", "Group.Read.All"],
    //       });

    //       await axios.post(
    //         "/api/auth/Ad",
    //         { Token: accessToken.accessToken },
    //         { withCredentials: true }
    //       );
          
    //       const { data: user } = await axios.post(
    //         "/api/auth/GetUserByCookie",
    //         {},
    //         { withCredentials: true }
    //       );

    //       await handlePostLogin(user);
    //     } else {
    //       // If no response, initiate the login
    //       await instance.loginRedirect({
    //         scopes: ["User.Read", "Group.Read.All"]
    //       });
    //     }
    //   } catch (error) {
    //     console.error("Error during login:", error);
    //     setIsLoading(false);
    //     showNotification({ message: t('authError'), type: NotificationType.Error });
    //   }
    // }
  };

  const handleChangeEmail = (e) => {
    const { name, value } = e.target;
    let noDiacritics = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    setFormData({ ...formData, [name]: noDiacritics });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    let auth = {
      username: formData.username,
      password: formData.password,
    };  
    
    try {
      await axios.post("/api/auth/Authenticate", {}, { auth, withCredentials: true });
      // email/heslo login – žiadna Graph fotka, použije sa pôvodný avatar
      localStorage.removeItem('userPhoto');
      const { data: user } = await axios.post("/api/auth/GetUserByCookie", {}, { withCredentials: true });

      await handlePostLogin(user);

    } catch (error) {
      if (error?.response?.status === 429) {
        // príliš veľa neúspešných pokusov – účet/IP dočasne zablokované (Retry-After v sekundách;
        // ak hlavičku prehliadač neprečíta, minúty sa vezmú z textu odpovede)
        const sec = parseInt(error.response.headers?.['retry-after'] || '0', 10);
        const fromDetail = parseInt((String(error.response.data?.detail || '').match(/(\d+)\s*min/) || [])[1] || '0', 10);
        const minutes = sec > 0 ? Math.ceil(sec / 60) : (fromDetail || 15);
        showNotification({ message: t('loginLocked').replace('{min}', minutes), type: NotificationType.Error });
      } else {
        showNotification({ message: t('authError'), type: NotificationType.Error });
      }
      setIsLoading(false);
    }
  };

  const handlePostLogin = async (user) => {
    const { id, userFirstname, userLastname, userEmail, isActive, roleId, authMethod, accesses } = user;
    applyUserLanguage(user);
    setAuth({
      uid: id, 
      ufn: userFirstname, 
      uln: userLastname, 
      uem: userEmail, 
      uia: isActive, 
      uri: roleId,
      uam: authMethod,
      uac: accesses
    });

    await validateAndSetCookie();
    
    navigate("/");
    //navigate(from);
  };

  const validateAndSetCookie = async () => {

    try {
      const environments = await getEnvironmentsforUser(); 

      if (!envCookie || !isValidEnvForUser(envCookie, environments)) {
        const defaultEnv = environments.length > 1 ? ALL_FOLDERS : (environments[0]?.Id || null); 
        if (defaultEnv) {
          setEnvCookie("ENV", defaultEnv, { maxAge: 86400 });
        }
      }
    } catch (error) {
      console.error("Error validating environment cookie:", error);
    }
  }

  // „Všetky moje priečinky“ je platná voľba pri 2+ priečinkoch; id z cookie je text -> porovnať ako text
  const isValidEnvForUser = (envCookie, environments) =>
    (environments.length > 1 && String(envCookie) === ALL_FOLDERS)
    || environments.some(env => String(env.Id) === String(envCookie));

  return (
    <div className='login-page'>

      <form onSubmit={handleSubmit} className="login-form">

        <div className="milladaTitle">
          <div className='text-drop'>
            <div className='mi'>Mi</div>
            <div className='ll'>
              <span className="stripe-1">l</span>
              <span className="stripe-2">l</span>
            </div>
            <div className='ada'>ada</div>
          </div>
        </div>
         
        <div className="milladaSubtitle">{t('login')}</div>
        <div className="login-inputs">
        {authConfig?.methods?.azure ?
          <button
            type="button"
            id="ad"
            disabled={isLoading}
            onClick={() => handleLogin("redirect")}>AD Sign In</button>
        : null }

        {(authConfig?.methods?.local || authConfig?.methods?.ldap) ?
          <>
          <div
            className="email-login-toggle"
            onClick={() => setShowEmailLogin(!showEmailLogin)}>
            <span>{t('emailSignIn')}</span>
            <span className={showEmailLogin ? "toggle-chevron open" : "toggle-chevron"}>&#9662;</span>
          </div>
          {showEmailLogin && (
          <div className="email-login-fields">
          <div>
            <input
              type="text"
              id="username"
              name="username"
              placeholder={t('email')}
              value={formData.username}
              onChange={handleChangeEmail}
              autoComplete="off"
            />
          </div>
          <div className="login-password-wrapper">
            <input
              type={showLoginPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder={t('password')}
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
            />
            {formData.password.length > 0 && (
              <button
                type="button"
                className={`login-password-toggle ${showLoginPassword ? 'is-visible' : 'is-hidden'}`}
                onClick={() => setShowLoginPassword(!showLoginPassword)}
                aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
              />
            )}
          </div>
          <button
            type="submit"
            id="logIn"
            disabled={isLoading}>
            {t('loginNow')}
            </button>
          </div>
          )}
          </>
        : null }
        </div>
        {isLoading ? <div className="lds-ellipsis">
          <div></div>
          <div></div><div></div><div></div></div> : ''}
      </form>
    </div>
  );
};

export default LoginForm;
